import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Link2, Plus, Sparkles } from "lucide-react";

import {
  MatchingPayload,
  PlanOperation,
  ResolutionNode,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import DecisionDialog, { DecisionSubject } from "./DecisionDialog";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * What the estate already holds of what the document describes — and what it would take to agree.
 *
 * Two halves, and the order is the argument. First per object: is this thing already there, is it
 * one of several, or is it new? That is the question a reader has, and each answer carries the
 * evidence for itself — which key decided, which fields agreed, which edge refused. Then the plan:
 * the same conclusions expressed as changes somebody could make.
 *
 * The plan is a preview and says so. Nothing here applies anything, and the absence of a button is
 * deliberate: a step that binds and creates in one motion gives nobody the chance to see what it
 * concluded before the estate has already changed.
 */

const STATUS_TONE: Record<string, string> = {
  resolved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  ambiguous: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  new: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
  unresolved: "bg-muted text-muted-foreground",
};

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation();

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${
        STATUS_TONE[status] ?? "bg-muted text-muted-foreground"
      }`}
    >
      {t(`documentAnalysisPanel.matching.status.${status}`)}
    </span>
  );
}

/** Why a node is not simply the best match — budget, a cycle, a broken containment edge. */
function ReasonBadge({ reason }: { reason?: string }) {
  const { t } = useTranslation();

  if (!reason || reason === "ambiguous") return null;

  return (
    <span className="rounded border border-amber-400/60 px-1.5 py-0.5 text-[11px] text-amber-700 dark:text-amber-300">
      {t(`documentAnalysisPanel.matching.reason.${reason}`, { defaultValue: reason })}
    </span>
  );
}


/**
 * The outcome in the step row, before anything is opened.
 *
 * The same reasoning as the extraction summary one step up: the first question about a result is
 * what is in it, and answering that should not cost a click. Here the answer has a shape — every
 * object came out one of three ways — so the list is grouped by outcome rather than by term.
 *
 * Which grouping is right depends on what is being asked. The cards below stay grouped by term,
 * because reading a document means following its own vocabulary; this line is asked how the run
 * went, and for that the three outcomes are the order.
 */
export function ResolutionSummary({ payload }: { payload: MatchingPayload }) {
  const { t } = useTranslation();

  const nodes = payload?.perNode ?? [];
  if (payload?.skipped || nodes.length === 0) return null;

  const groups: Array<{ key: string; tone: string; nodes: ResolutionNode[] }> = [
    {
      key: "found",
      tone: "text-emerald-700 dark:text-emerald-300",
      nodes: nodes.filter((node) => node.status === "resolved"),
    },
    {
      key: "decide",
      tone: "text-amber-700 dark:text-amber-300",
      // Both kinds of open question in one group: from here they are the same thing — somebody has
      // to look. Why they are open is on the card.
      nodes: nodes.filter((node) => node.status === "ambiguous" || node.status === "unresolved"),
    },
    {
      key: "new",
      tone: "text-muted-foreground",
      nodes: nodes.filter((node) => node.status === "new"),
    },
  ];

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {groups
        .filter((group) => group.nodes.length > 0)
        .map((group) => (
          <div key={group.key}>
            <div className={`text-[11px] font-medium ${group.tone}`}>
              {t(`documentAnalysisPanel.matching.group.${group.key}`, { count: group.nodes.length })}
            </div>

            <ul className="pl-4">
              {group.nodes.map((node) => {
                const bound = node.candidates.find(
                  (candidate) => candidate.objectId === node.boundObjectId,
                );

                return (
                  <li
                    key={node.extractedObjectId}
                    className="grid grid-cols-[minmax(0,7rem)_minmax(0,6rem)_minmax(0,1fr)] gap-x-3 text-[11px]"
                  >
                    <span className="truncate" title={node.term}>
                      {node.term}
                    </span>
                    <span className="truncate font-mono text-muted-foreground">
                      {node.className ?? t("documentAnalysisPanel.test.withoutClass")}
                    </span>

                    <span className="min-w-0 truncate">
                      {node.status === "resolved" ? (
                        <>
                          <span>{bound?.label || t("documentAnalysisPanel.matching.unnamed")}</span>{" "}
                          <span className="font-mono text-muted-foreground" title={node.boundObjectId ?? ""}>
                            …{(node.boundObjectId ?? "").slice(-8)}
                          </span>
                        </>
                      ) : node.candidates.length > 0 ? (
                        <span className="text-amber-700 dark:text-amber-300">
                          {t("documentAnalysisPanel.matching.candidateCount", {
                            count: node.candidates.length,
                          })}
                          {node.candidates[0]?.label ? ` · ${node.candidates[0].label}` : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("documentAnalysisPanel.matching.wouldBeCreated")}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
    </div>
  );
}

function ResolutionCard({
  node,
  onOpen,
  decidedAs,
}: {
  node: ResolutionNode;
  /** Absent where a decision cannot be recorded — then the card states rather than asks. */
  onOpen?: (node: ResolutionNode) => void;
  /** Set once somebody answered here: the card says so instead of asking again. */
  decidedAs?: string | null;
}) {
  const { t } = useTranslation();

  // The bound one by id rather than by position: the band is ordered by distance, and after a graph
  // tiebreak the survivor is not necessarily the one that was closest on attributes.
  const bound = node.candidates.find((candidate) => candidate.objectId === node.boundObjectId);

  const decides = node.status === "ambiguous" || node.status === "unresolved";

  return (
    <div
      className={`rounded-md border bg-background p-3 ${
        decides ? "border-l-4 border-l-amber-500" : ""
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{node.term}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {node.className ?? t("documentAnalysisPanel.test.withoutClass")}
        </span>
        <StatusChip status={node.status} />
        <ReasonBadge reason={node.reason} />
        {node.boundBy === "auto" ? (
          <span className="text-[11px] text-muted-foreground">
            {t("documentAnalysisPanel.matching.boundAutomatically")}
          </span>
        ) : null}
      </div>

      {node.status === "resolved" && node.boundObjectId ? (
        <div className="rounded border border-emerald-500/40 bg-emerald-50/60 p-2 dark:bg-emerald-950/30">
          <div className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
            <Link2 className="h-3 w-3" aria-hidden />
            {t("documentAnalysisPanel.matching.boundTo")}
          </div>

          <div className="text-sm font-medium">{bound?.label || t("documentAnalysisPanel.matching.unnamed")}</div>

          {/* The id, shortened but complete on hover: the reader has to be able to go and look at
              the object that was bound, and a label alone does not identify a row. */}
          <div className="flex flex-wrap items-baseline gap-2 text-[11px] text-muted-foreground">
            <span className="font-mono">{node.boundClassName}</span>
            <span className="font-mono" title={node.boundObjectId}>
              …{node.boundObjectId.slice(-8)}
            </span>
            {bound ? (
              <span>
                {t("documentAnalysisPanel.matching.via", { key: bound.keyName })}
                {bound.matchedOn.length > 0 ? ` · ${bound.matchedOn.join(", ")}` : ""}
              </span>
            ) : null}
            {bound ? <span>{Math.round(bound.score * 100)} %</span> : null}
          </div>
        </div>
      ) : null}

      {node.status !== "resolved" && node.candidates.length > 0 ? (
        <div className="rounded border border-amber-400/60 bg-amber-50/60 p-2 dark:bg-amber-950/30">
          {/* What is being asked, in words. A list of candidates without the question reads as a
              result; it is a question, and the reader is the one who answers it. */}
          <div className="mb-1 text-[11px] font-medium text-amber-900 dark:text-amber-200">
            {node.reason === "lowEvidence"
              ? t("documentAnalysisPanel.matching.askLowEvidence", { count: node.candidates.length })
              : t("documentAnalysisPanel.matching.askWhich", { count: node.candidates.length })}
          </div>

          {/* The candidates are named, not tabulated: five columns of values do not fit a card in
              a stepper, and the dialog is the right size for them. */}
          <ul className="mb-2">
            {node.candidates.slice(0, 3).map((candidate, index) => (
              <li key={candidate.objectId} className="truncate text-xs">
                {index + 1}. {candidate.label || t("documentAnalysisPanel.matching.unnamed")}{" "}
                <span className="text-muted-foreground">{Math.round(candidate.score * 100)} %</span>
              </li>
            ))}
            {node.candidates.length > 3 ? (
              <li className="text-xs text-muted-foreground">
                {t("documentAnalysisPanel.matching.andMore", { count: node.candidates.length - 3 })}
              </li>
            ) : null}
          </ul>

          {decidedAs !== undefined ? (
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {decidedAs === null
                ? t("documentAnalysisPanel.matching.decidedNone")
                : t("documentAnalysisPanel.matching.decidedBound")}
            </p>
          ) : onOpen ? (
            <Button size="sm" variant="outline" onClick={() => onOpen(node)}>
              {t("documentAnalysisPanel.matching.decide")}
            </Button>
          ) : (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("documentAnalysisPanel.matching.decisionLater")}
            </p>
          )}
        </div>
      ) : null}

      {node.status === "new" ? (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" aria-hidden />
          {t("documentAnalysisPanel.matching.notInEstate")}
        </p>
      ) : null}

      {/* Why nothing sharper was asked. Without this a bare "new" is unaccountable — the reader
          cannot tell a thing that is absent from a key that could not be evaluated. */}
      {node.skippedKeys.length > 0 ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="mt-1 cursor-default text-[11px] text-muted-foreground underline decoration-dotted">
                {t("documentAnalysisPanel.matching.skippedKeys", { count: node.skippedKeys.length })}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <ul className="list-inside list-disc text-xs">
                {node.skippedKeys.map((entry, index) => (
                  <li key={index}>{entry}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

function OperationRow({ operation }: { operation: PlanOperation }) {
  const { t } = useTranslation();

  if (operation.op === "updateAttributes") {
    return (
      <div className="border-t py-2 first:border-0">
        <div className="mb-1 flex flex-wrap items-baseline gap-2 text-xs">
          <span className="font-medium">{t("documentAnalysisPanel.matching.op.updateAttributes")}</span>
          <span className="font-mono text-muted-foreground">
            {operation.target?.className} {String(operation.target?.id).slice(-6)}
          </span>
        </div>
        <table className="w-full text-xs">
          <tbody>
            {(operation.changes ?? []).map((change) => (
              <tr key={change.field}>
                <td className="w-[40%] py-0.5 pr-2 font-mono text-muted-foreground">{change.field}</td>
                <td className="py-0.5">
                  <span className="text-muted-foreground line-through">{formatValue(change.old)}</span>{" "}
                  → <span className="font-medium">{formatValue(change.new)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (operation.op === "createObject") {
    const filled = Object.keys(operation.attributes ?? {}).filter(
      (field) => (operation.attributes ?? {})[field] !== null,
    );

    return (
      <div className="border-t py-2 first:border-0">
        <div className="flex flex-wrap items-baseline gap-2 text-xs">
          <Plus className="h-3 w-3" aria-hidden />
          <span className="font-medium">{t("documentAnalysisPanel.matching.op.createObject")}</span>
          <span className="font-mono">{operation.className}</span>
          {typeof operation.confidence === "number" ? (
            <span className="text-muted-foreground">{Math.round(operation.confidence * 100)} %</span>
          ) : null}
          {operation.requiresReview ? (
            <span className="rounded bg-amber-100 px-1.5 text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {t("documentAnalysisPanel.matching.requiresReview")}
            </span>
          ) : null}
        </div>

        <p className="text-xs text-muted-foreground">
          {filled.length > 0 ? filled.join(", ") : t("documentAnalysisPanel.test.nothingFound")}
        </p>

        {(operation.references ?? []).map((reference, index) => (
          <p key={index} className="text-xs text-muted-foreground">
            → {reference.name}: {reference.target.className} {String(reference.target.id).slice(-6)}
          </p>
        ))}
      </div>
    );
  }

  if (operation.op === "createReference" || operation.op === "confirmReference") {
    return (
      <div className="flex flex-wrap items-baseline gap-2 border-t py-1 text-xs first:border-0">
        <Link2 className="h-3 w-3" aria-hidden />
        <span className="font-medium">
          {t(`documentAnalysisPanel.matching.op.${operation.op}`)}
        </span>
        <span className="font-mono text-muted-foreground">
          {operation.source?.className} —{operation.name}→ {operation.target?.className}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-baseline gap-2 border-t py-1 text-xs first:border-0">
      <span className="font-medium">{t("documentAnalysisPanel.matching.op.unresolved")}</span>
      <ReasonBadge reason={operation.reason} />
      <span className="text-muted-foreground">
        {t("documentAnalysisPanel.matching.candidateCount", {
          count: operation.candidates?.length ?? 0,
        })}
      </span>
    </div>
  );
}

export default function ResolutionCards({ payload }: { payload: MatchingPayload }) {
  const { t } = useTranslation();
  const api = useDocumentAnalysisAPI();

  const [planOpen, setPlanOpen] = useState(false);
  const [subject, setSubject] = useState<DecisionSubject | null>(null);
  /** What was decided while this result has been on screen — the payload itself never refreshes. */
  const [decided, setDecided] = useState<Record<string, string | null>>({});

  if (payload?.skipped) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("documentAnalysisPanel.test.skipped", { reason: payload.reason ?? "" })}
      </p>
    );
  }

  const nodes = payload?.perNode ?? [];
  const operations = payload?.operations ?? [];
  const summary = payload?.summary;

  const download = () => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify({ summary, operations, perNode: nodes, config: payload.config }, null, 2)], {
        type: "application/json",
      }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "bindungsplan.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-3">
      {summary ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t("documentAnalysisPanel.matching.summaryResolved", { count: summary.resolved })}</span>
          <span>· {t("documentAnalysisPanel.matching.summaryNew", { count: summary.new })}</span>
          <span>· {t("documentAnalysisPanel.matching.summaryAmbiguous", { count: summary.ambiguous })}</span>
          {summary.reviewRequired > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              · {t("documentAnalysisPanel.matching.summaryReview", { count: summary.reviewRequired })}
            </span>
          ) : null}
        </div>
      ) : null}

      {nodes.map((node) => (
        <ResolutionCard
          key={node.extractedObjectId}
          node={node}
          decidedAs={decided[node.extractedObjectId]}
          onOpen={(entry) =>
            setSubject({
              extractedObjectId: entry.extractedObjectId,
              term: entry.term,
              className: entry.className,
              candidates: entry.candidates,
              searchedKey: entry.searchedKey,
              searchedValues: entry.searchedValues,
              boundObjectId: decided[entry.extractedObjectId] ?? null,
            })
          }
        />
      ))}

      {operations.length > 0 ? (
        <div className="rounded-md border">
          <button
            type="button"
            className="flex w-full items-center gap-2 p-2 text-left text-sm font-medium"
            aria-expanded={planOpen}
            onClick={() => setPlanOpen(!planOpen)}
          >
            {planOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t("documentAnalysisPanel.matching.plan")}
            <span className="text-xs font-normal text-muted-foreground">
              {t("documentAnalysisPanel.matching.planCounts", {
                updates: summary?.updates ?? 0,
                creates: summary?.creates ?? 0,
              })}
            </span>
          </button>

          {planOpen ? (
            <div className="border-t p-2">
              {operations.map((operation, index) => (
                <OperationRow key={index} operation={operation} />
              ))}

              <div className="mt-3 flex items-center justify-between gap-2 border-t pt-2">
                {/* No apply button, and the sentence says why rather than leaving a gap. */}
                <p className="text-xs text-muted-foreground">
                  {t("documentAnalysisPanel.matching.previewOnly")}
                </p>
                <Button variant="outline" size="sm" onClick={download}>
                  {t("documentAnalysisPanel.matching.downloadPlan")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <DecisionDialog
        subject={subject}
        onClose={() => setSubject(null)}
        onDecide={async (entry, candidate) => {
          if (candidate) {
            await api.bindExtractedObject(entry.extractedObjectId, candidate.objectId, candidate.className);
          } else {
            await api.rejectExtractedObject(entry.extractedObjectId);
          }

          setDecided((current) => ({
            ...current,
            [entry.extractedObjectId]: candidate?.objectId ?? null,
          }));
        }}
      />
    </div>
  );
}
